import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import Col from 'antd/lib/col';
import FormItem from 'antd/lib/form/FormItem';
import FormItemLabel from 'antd/lib/form/FormItemLabel';
import TimePicker from 'antd/lib/time-picker';
import DatePicker from 'antd/lib/date-picker';
import { disableDates, formatDate } from '../../utils/MiscUtils';
import { DATE_FORMAT, DATE_FORMAT_SERVER, HALF_BLOCK_COLSPAN, updateFormData } from '../../utils/Constants';
import { RESTRICTIONS_TABLE_TIME_FORMAT } from '../../views/SearchesManager/SearchManagerRule/RuleAppliesTo/SMDateTimeRestrictions/RestrictionsTable/TimeInputsRow/TimeInputsRow';
import styles from './EffectiveDateRangePickerCol.module.css';
import '../../views/ShoppingDiagnostics/SDForm/SDLegs/SDLegs.css';
import { TimeOfDay } from '../../integrated-client';

const getValidateStatus = (validation: EffectiveDateRangePickerColValidation) => (validation ? 'error' : 'success');

const getValidationMessage = (validation: EffectiveDateRangePickerColValidation) => (typeof validation === 'string' ? validation : null);

type EffectiveDateRangePickerColValidation = string | boolean | null | undefined;

type DisabledTime = (now: dayjs.Dayjs) => {
  disabledHours?: () => number[];
  disabledMinutes?: (selectedHour: number) => number[];
  disabledSeconds?: (selectedHour: number, selectedMinute: number) => number[];
};

interface IEffectiveDateRangePickerColProps {
  fieldName: string;
  label?: string;
  onChange: updateFormData;
  date: Date | object | null | undefined;
  time?: TimeOfDay | object | null | undefined;
  validation: EffectiveDateRangePickerColValidation;
  disabledDatesThreshold?: Date | object | null | undefined;
  disabledDatesType?: 'to' | 'from' | null | undefined;
  onlyDates?: boolean;
  colSpan?: number;
  allowClear?: boolean;
  onlyTimes?: boolean;
  id?: string;
  disableTimeThreshold?: TimeOfDay | object | null | undefined;
  disabled?: boolean;
  // Replaces the threshold-based check when the caller needs its own rules
  disabledDate?: (date: dayjs.Dayjs) => boolean;
}

const EffectiveDateRangePickerCol: React.FC<IEffectiveDateRangePickerColProps> = ({
  label,
  onChange,
  fieldName,
  date,
  time,
  validation,
  disabledDatesThreshold,
  disabledDatesType,
  onlyDates = false,
  colSpan = HALF_BLOCK_COLSPAN,
  allowClear = true,
  onlyTimes = false,
  id = '',
  disableTimeThreshold,
  disabledDate,
}) => {
  const handleDateTimeSelect = (value?: dayjs.Dayjs | null) => {
    let date = value ? value?.toDate() : value;
    if (date === null) {
      date = undefined;
    }

    const result: any = { [fieldName]: date };
    if (id) {
      result.id = id;
    }
    return onChange(result);
  };

  const handleTimeSelect = (value?: dayjs.Dayjs | null) => {
    let time = value ? value?.format('HH:mm') : value;
    if (time === null) {
      const result: any = { [fieldName]: undefined };
      if (id) {
        result.id = id;
      }
      return onChange(result);
    }
    const [hours, minutes] = time!.split(':').map(Number);

    const result: any = { [fieldName]: { hours: hours, minutes: minutes } };
    if (id) {
      result.id = id;
    }
    return onChange(result);
  };

  const disabledThresholdTime = useMemo(() => {
    if (disableTimeThreshold) {
      if (typeof disableTimeThreshold === 'object' && 'hours' in disableTimeThreshold && 'minutes' in disableTimeThreshold) {
        return dayjs(`${disableTimeThreshold.hours}:${disableTimeThreshold.minutes}`, RESTRICTIONS_TABLE_TIME_FORMAT);
      }
      return dayjs(disableTimeThreshold as any);
    }

    return dayjs(`00:00`, RESTRICTIONS_TABLE_TIME_FORMAT);
  }, [disableTimeThreshold]);

  const momentDate = useMemo(() => {
    if (date) {
      return dayjs(date as any);
    }
    return null;
  }, [date]);

  const disabledThresholdDate = useMemo(() => {
    if (disabledDatesThreshold) {
      return dayjs(disabledDatesThreshold as any);
    }
    return null;
  }, [disabledDatesThreshold]);

  const momentTime = (time: TimeOfDay | object | null | undefined) => {
    if (time) {
      // Handle TimeOfDay object { hours, minutes }
      if (typeof time === 'object' && 'hours' in time && 'minutes' in time) {
        const t: any = time as any;
        return dayjs(`${t.hours}:${t.minutes}`, RESTRICTIONS_TABLE_TIME_FORMAT);
      }
      // Handle string or other formats
      if (typeof time === 'string') return dayjs(time, RESTRICTIONS_TABLE_TIME_FORMAT);
      try {
        return dayjs((time as any).toString(), RESTRICTIONS_TABLE_TIME_FORMAT);
      } catch {
        return dayjs(`00:00`, RESTRICTIONS_TABLE_TIME_FORMAT);
      }
    }

    return dayjs(`00:00`, RESTRICTIONS_TABLE_TIME_FORMAT);
  };

  const disableTime: DisabledTime = (disabledThresholdTime: dayjs.Dayjs | undefined | null) => {
    if (!disabledThresholdTime) {
      return {
        disabledHours: () => [],
        disabledMinutes: () => [],
        disabledSeconds: () => [],
      };
    }

    const hours = disabledThresholdTime.hour();
    const minutes = disabledThresholdTime.minute();

    return {
      disabledHours: () => Array.from(Array(hours).keys()),
      disabledMinutes: (selectedHour: number) => selectedHour === hours ? Array.from(Array(minutes).keys()) : [],
      disabledSeconds: () => [],
    };
  };

  if (!onlyTimes) {
    return (
      <>
        <Col span={colSpan === 0 ? undefined : colSpan} className={`${fieldName}Col`}>
          <FormItem className={styles.inputWrapper} help={getValidationMessage(validation)} validateStatus={getValidateStatus(validation)}>
            <FormItemLabel prefixCls={fieldName} label={label} />
            <DatePicker
              format={DATE_FORMAT}
              allowClear={allowClear}
              value={momentDate}
              onChange={handleDateTimeSelect}
              disabledDate={disabledDate ?? ((momentDate) => disableDates(momentDate, disabledThresholdDate, disabledDatesType))}
            />
            {!onlyDates && (
              <TimePicker
                className={styles.timepicker}
                allowClear={allowClear}
                format={RESTRICTIONS_TABLE_TIME_FORMAT}
                value={momentDate}
                onChange={handleDateTimeSelect}
              />
            )}
          </FormItem>
        </Col>
      </>
    );
  } else {
    return (
      <>
        <Col span={colSpan === 0 ? undefined : colSpan} className={`${fieldName}Col`}>
          <FormItem className={styles.inputWrapper} help={getValidationMessage(validation)} validateStatus={getValidateStatus(validation)}>
            <FormItemLabel prefixCls={fieldName} label={label} />
            <TimePicker
              allowClear={allowClear}
              format={RESTRICTIONS_TABLE_TIME_FORMAT}
              value={momentTime(time)}
              onChange={handleTimeSelect}
              disabledTime={() => disableTime(disabledThresholdTime)}
            />
          </FormItem>
        </Col>
      </>
    );
  }
};

export default EffectiveDateRangePickerCol;
